import React, { useEffect, useMemo, useState } from 'react';
import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  TruckIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';

function displayValue(value) {
  return value || 'Not provided';
}

function formatDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not available'
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function DriverProfiles() {
  const [drivers, setDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(API_URLS.admin.drivers);
      setDrivers(response.data);
    } catch (err) {
      console.error('Error fetching driver profiles:', err);
      setError(err.response?.data?.message || 'Failed to load driver profiles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, []);

  const filteredDrivers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return drivers;

    return drivers.filter((driver) => [
      driver.firstName,
      driver.lastName,
      driver.email,
      driver.phone,
      driver.bio,
      ...(driver.vehicles || []).flatMap((vehicle) => [
        vehicle.make,
        vehicle.model,
        vehicle.licensePlate
      ])
    ].some((value) => value?.toLowerCase().includes(query)));
  }, [drivers, searchTerm]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Driver Profiles</h1>
        <p className="mt-2 text-sm text-gray-600">
          Driver contact information, approval status, and registered vehicles.
        </p>
      </div>

      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <label htmlFor="driver-search" className="sr-only">Search drivers</label>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            id="driver-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, contact, bio, or vehicle"
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center" role="status" aria-label="Loading driver profiles">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          <p>{error}</p>
          <button
            type="button"
            onClick={fetchDrivers}
            className="mt-3 rounded bg-red-700 px-3 py-1.5 font-medium text-white hover:bg-red-800"
          >
            Try Again
          </button>
        </div>
      ) : filteredDrivers.length === 0 ? (
        <div className="rounded-lg bg-white px-6 py-12 text-center shadow">
          <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-3 text-sm font-semibold text-gray-900">
            {drivers.length ? 'No drivers match your search' : 'No driver profiles found'}
          </h2>
          {drivers.length > 0 && <p className="mt-1 text-sm text-gray-500">Try another name, contact, or vehicle.</p>}
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-gray-500">{filteredDrivers.length} driver{filteredDrivers.length === 1 ? '' : 's'}</p>
          {filteredDrivers.map((driver) => {
            const status = driver.status || 'active';
            const isApproved = driver.isAdminApproved;
            return (
              <article key={driver.id} className="overflow-hidden rounded-lg bg-white shadow">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-5 py-5 sm:px-6">
                  <div className="flex items-center gap-3">
                    <UserCircleIcon className="h-12 w-12 flex-shrink-0 text-indigo-500" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {driver.firstName} {driver.lastName}
                      </h2>
                      <p className="text-sm text-gray-500">Joined {formatDate(driver.createdAt)}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      status === 'suspended'
                        ? 'bg-red-100 text-red-800'
                        : status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      isApproved ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {isApproved ? 'Approved' : 'Pending approval'}
                    </span>
                  </div>
                </div>

                <div className="grid gap-6 px-5 py-5 sm:grid-cols-2 sm:px-6">
                  <section aria-label="Driver contact and profile">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Contact & Profile</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <a className="break-all text-indigo-700 hover:underline" href={`mailto:${driver.email}`}>{displayValue(driver.email)}</a>
                      </div>
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <a className="text-indigo-700 hover:underline" href={`tel:${driver.phone}`}>{displayValue(driver.phone)}</a>
                      </div>
                      <div>
                        <dt className="font-medium text-gray-700">Bio</dt>
                        <dd className="mt-1 whitespace-pre-wrap text-gray-600">{displayValue(driver.bio)}</dd>
                      </div>
                    </dl>
                  </section>

                  <section aria-label="Driver vehicles">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      <TruckIcon className="h-4 w-4" /> Vehicles ({driver.vehicles?.length || 0})
                    </h3>
                    {driver.vehicles?.length ? (
                      <ul className="space-y-3">
                        {driver.vehicles.map((vehicle) => (
                          <li key={vehicle.id} className="rounded-md border border-gray-200 p-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="font-medium text-gray-900">
                                {vehicle.year} {vehicle.color} {vehicle.make} {vehicle.model}
                              </p>
                              {vehicle.isDefault && (
                                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Default</span>
                              )}
                            </div>
                            <p className="mt-1 text-sm text-gray-600">
                              Plate: {displayValue(vehicle.licensePlate)} · Capacity: {vehicle.capacity ?? 'Not provided'}
                            </p>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500">No vehicle information available.</p>
                    )}
                  </section>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <section className="mt-8 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/60 p-5 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <span className="inline-flex w-fit rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-800">
            Coming soon
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Planned driver profile features</h2>
            <p className="mt-1 text-sm text-gray-600">Future updates are planned to include:</p>
            <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-gray-700">
              <li>A consolidated view of their rides/bookings.</li>
              <li>Approve/suspend/delete actions, and edit capabilities.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default DriverProfiles;
